import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { products as defaultProducts } from "@/data/products";
import { loadWebsiteCatalog } from "@/data/productApi";
import { cloneDefaultSiteContent } from "@/data/siteContent";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { ProductShowcase } from "@/components/ProductShowcase";
import { ProductModal } from "@/components/ProductModal";
import { Comparison } from "@/components/Comparison";
import { Technology } from "@/components/Technology";
import { WhyChoose } from "@/components/WhyChoose";
import { AboutTimeline } from "@/components/AboutTimeline";
import { DealerNetwork } from "@/components/DealerNetwork";
import { LeadForm } from "@/components/LeadForm";
import { FAQ } from "@/components/FAQ";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { ProductEditor } from "@/components/ProductEditor";
import { Seo } from "@/components/Seo";

function App() {
  const isEditor = /^\/editor\/?$/.test(window.location.pathname);
  const [products, setProducts] = useState(defaultProducts);
  const [content, setContent] = useState(() => cloneDefaultSiteContent());
  const [modalProduct, setModalProduct] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [compareRequest, setCompareRequest] = useState(null);

  const scrollTo = useCallback((id, behavior = "smooth") => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior, block: "start" });
  }, []);

  useEffect(() => {
    if (isEditor) return undefined;
    let active = true;
    loadWebsiteCatalog().then((website) => {
      if (active) {
        setProducts(website.products);
        setContent(website.content);
      }
    });
    return () => {
      active = false;
    };
  }, [isEditor]);

  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    const timer = window.setTimeout(() => scrollTo(id, "auto"), 120);
    return () => window.clearTimeout(timer);
  }, [scrollTo]);

  // Preselect a model (and optionally buyer/enquiry type) then scroll to form
  const handleRegister = useCallback(
    (modelName, buyerType, enquiryType) => {
      setPrefill({ model: modelName || undefined, buyerType, enquiryType, ts: Date.now() });
      scrollTo("register");
    },
    [scrollTo]
  );

  const handleCompare = useCallback(
    (id) => {
      setCompareRequest({ id, ts: Date.now() });
      scrollTo("compare");
    },
    [scrollTo]
  );

  if (isEditor) return <ProductEditor />;

  return (
    <div className="App bg-surface-deep text-white min-h-screen">
      <Seo products={products} content={content} />
      <Header content={content} onScrollTo={scrollTo} />
      <main>
        <Hero content={content} onScrollTo={scrollTo} />
        <ProductShowcase
          products={products}
          content={content}
          onOpen={setModalProduct}
          onRegister={handleRegister}
          onCompare={handleCompare}
        />
        <Comparison products={products} content={content} compareRequest={compareRequest} onScrollTo={scrollTo} />
        <Technology content={content} />
        <WhyChoose content={content} />
        <AboutTimeline content={content} />
        <DealerNetwork content={content} onRegister={handleRegister} onScrollTo={scrollTo} />
        <LeadForm products={products} content={content} prefill={prefill} />
        <Contact content={content} />
        <FAQ content={content} />
      </main>
      <Footer products={products} content={content} onScrollTo={scrollTo} onRegister={handleRegister} />

      {modalProduct && (
        <ProductModal
          product={modalProduct}
          content={content}
          onClose={() => setModalProduct(null)}
          onRegister={handleRegister}
        />
      )}
    </div>
  );
}

export default App;
